import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./MosPublicPage.module.css";
import MOSFragebogen from "../MOSFragebogen/MOSFragebogen";
import { MOSFragebogenPublicProvider } from "../../context/MOSFragebogenContext";

const API = process.env.REACT_APP_API_URL;

// States: "loading" | "invalid" | "locked" | "form" | "landing" | "questionnaire"

export default function MosPublicPage() {
  const { token } = useParams();
  const [view, setView]                   = useState("loading");
  const [code, setCode]                   = useState("");
  const [error, setError]                 = useState("");
  const [submitting, setSub]              = useState(false);
  const [stage, setStage]                 = useState("vorerhebung");
  const [fragebogenId, setFragebogenId]   = useState(null);
  const [prefillName, setPrefillName]     = useState(null);
  const [prefillVorname, setPrefillVorname] = useState(null);
  const [episodeCount, setEpisodeCount]   = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/mos/check/${token}`)
      .then((r) => r.json())
      .then((data) => {
        setView(data.status === "valid" ? "form" : data.status === "locked" ? "locked" : "invalid");
      })
      .catch(() => setView("invalid"));
  }, [token]);

  useEffect(() => {
    if (view === "form") inputRef.current?.focus();
  }, [view]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSub(true);
    try {
      const res = await fetch(`${API}/api/mos/verify/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setStage(data.stage || "vorerhebung");
        setFragebogenId(data.fragebogen_id || null);
        setPrefillName(data.name || null);
        setPrefillVorname(data.vorname || null);
        setEpisodeCount(data.episode_count ?? 0);
        // EK: show landing screen; VE: go straight to questionnaire
        setView(data.stage === "erfolgskontrolle" ? "landing" : "questionnaire");
      } else if (data.locked) {
        setView("locked");
      } else {
        setError(data.message || "Falscher Code. Bitte erneut versuchen.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuchen Sie es erneut.");
    } finally {
      setSub(false);
    }
  };

  if (view === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.loading}>Wird geladen…</p>
        </div>
      </div>
    );
  }

  if (view === "invalid") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Ungültiger Link</h1>
          <p className={styles.message}>
            Dieser Link ist ungültig oder nicht mehr aktiv.
          </p>
        </div>
      </div>
    );
  }

  if (view === "locked") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Zugang gesperrt</h1>
          <p className={styles.message}>
            Der Code wurde zu oft falsch eingegeben. Bitte kontaktieren Sie den Administrator, um den Zugang wieder freizuschalten.
          </p>
        </div>
      </div>
    );
  }

  if (view === "landing") {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.landingIcon}>✓</div>
          <h1 className={styles.title}>Willkommen zurück!</h1>
          <p className={styles.subtitle}>
            {episodeCount > 0
              ? `Sie haben bisher ${episodeCount} Erfolgskontrolle${episodeCount !== 1 ? "n" : ""} ausgefüllt.`
              : "Ihre Vorerhebung ist bereits erfasst."}
          </p>
          <p className={styles.message}>
            Klicken Sie auf den Button, wenn Sie bereit sind.
          </p>
          <button
            className={styles.btn}
            onClick={() => setView("questionnaire")}
          >
            Erfolgskontrolle starten
          </button>
        </div>
      </div>
    );
  }

  if (view === "questionnaire") {
    return (
      <MOSFragebogenPublicProvider>
        <MOSFragebogen
          token={token}
          code={code.trim()}
          startAtEK={stage === "erfolgskontrolle"}
          fragebogenId={fragebogenId}
          prefillName={prefillName}
          prefillVorname={prefillVorname}
        />
      </MOSFragebogenPublicProvider>
    );
  }

  // view === "form" — code entry
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>MOS Fragebogen</h1>
        <p className={styles.subtitle}>
          Bitte geben Sie Ihren Zugangscode ein, um fortzufahren.
        </p>
        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Zugangscode"
            maxLength={20}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            required
          />
          {error && <p className={styles.error}>{error}</p>}
          <button
            type="submit"
            className={styles.btn}
            disabled={submitting || !code.trim()}
          >
            {submitting ? "Wird geprüft…" : "Bestätigen"}
          </button>
        </form>
      </div>
    </div>
  );
}
